import { useContext, createContext, memo } from "react"

interface FormContextType {
    disableInputs: boolean
    isLoading: boolean
}

const FormContext = createContext<FormContextType | undefined>(undefined)

// Hook for children to use the form context
export const useFormContext = () => {
    const context = useContext(FormContext)
    if (!context) {
        // The console logging should be commented out due to
        // the wast of console logs.
        //console.warn('useFormContext must be used within a Form component')
        return {} as FormContextType
    }
    return context
}

interface FormProps {
    children: React.ReactNode
    className?: string
    onSubmit: (e: React.FormEvent) => void
    disableInputs?: boolean
    isLoading?: boolean
}

// Form component definition
const Form: React.FC<FormProps> = ({ children, className = "", onSubmit, disableInputs = false, isLoading = false }) => {

    const contextValue: FormContextType = {
        'disableInputs': disableInputs ? true : isLoading ? true : false,
        isLoading
    }

    // Handle keydown to submit on Ctrl+Enter or Cmd+Enter
    const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            onSubmit(e as any)
        }
    }

    // Render the form component
    return (
        <FormContext.Provider value={contextValue}>
            <form
                onSubmit={onSubmit}
                onKeyDown={handleKeyDown}
                className={`p-0 bg-inherit border-0 rounded-none static ${className}`}
            >
                {children}
            </form>
        </FormContext.Provider>
    )
}

export default memo(Form)
